import configs from 'configs';
import request from 'functions/api/request';
import { authHeaders } from 'functions/authHeader';
import {
  SurveyAnswerKeys, SurveyAppointmentRepeatAnswerValues, SurveyStatus, SurveyTypes,
} from './surveyEnums';

const { beApiUrl: apiUrl } = configs;

export async function getSurvey(surveyName, userWixId) {
  const requestOptions = {
    url: `${apiUrl}/survey/${userWixId}/${surveyName}`,
    headers: await authHeaders(),
    json: true,
  };

  try {
    return await request.get(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while getting Treatment Experience survey:', ex);
    throw ex;
  }
}

/**
 *
 * @param {*} surveyName
 * @param {*} answers // following format
 * [
 *  {
 *    surveyKey: 'string',
 *    key: 'string',
 *    value: 'string'
 *  }
 * ]
 * @param {*} userWixId
 * @returns
 */

export async function setSurvey(surveyName, answers, userWixId) {
  const requestOptions = {
    url: `${apiUrl}/survey/${userWixId}/${surveyName}`,
    headers: await authHeaders(),
    data: answers,
    json: true,
  };

  try {
    return await request.post(requestOptions);
  } catch (ex) {
    console.error('Error occurred, while setting Treatment Experience survey:', ex);
    throw ex;
  }
}

export const addSurveyEvent = async ({ data, userWixId }) => {
  let nextAppointmentValue = '';

  if (data.month && data.day && data.year) { // Check if all date components are present
    const formatDate = (value) => (value < 10 && value.length === 1 ? `0${value}` : value);
    nextAppointmentValue = `${formatDate(data.month)}/${formatDate(data.day)}/${data.year}`;
  }
  // Transform frontend data to match backend SurveyDto
  const surveyData = {
    userWixId,
    type: SurveyTypes[data.eventType] || SurveyTypes.Scan, // Default to Scan if eventType is invalid
    surveyAnswers: [
      {
        surveyKey: 'appointmentDate',
        key: SurveyAnswerKeys.nextAppointment,
        value: nextAppointmentValue, // Use the conditionally set value
      },
      {
        surveyKey: 'treatmentCycle',
        key: SurveyAnswerKeys.cycle,
        value: data.cycle,
      },
      {
        surveyKey: 'repeatFrequency',
        key: SurveyAnswerKeys.repeat,
        value: SurveyAppointmentRepeatAnswerValues[data.repeatStatus] || SurveyAppointmentRepeatAnswerValues.none,
      },
    ],
  };

  // Special handling for "scan" eventType: Remove cycle and repeat if not relevant
  if (data.eventType === 'scan') {
    surveyData.surveyAnswers = surveyData.surveyAnswers.filter((answer) => answer.key === SurveyAnswerKeys.nextAppointment);
  }

  const requestOptions = {
    url: `${apiUrl}/survey`,
    headers: await authHeaders(),
    data: surveyData,
    json: true,
  };

  try {
    const response = await request.post(requestOptions);
    if (response && response.success) {
      return { message: 'Event Added', newEvent: response.data };
    }
    console.error('Error adding event:', response?.error || 'Unknown error');
    throw new Error('Failed to add event');
  } catch (ex) {
    console.error('Error occurred while adding event', ex);
    throw ex;
  }
};

export const deleteSurveyEvent = async (id) => {
  const requestOptions = {
    url: `${apiUrl}/survey/${id}`,
    headers: await authHeaders(),
    json: true,
  };

  try {
    const response = await request.del(requestOptions);
    if (response && response.success) {
      return { message: 'Event Deleted', id };
    }
    console.error('Error deleting event:', response?.error || 'Unknown error');
    throw new Error('Failed to delete event');
  } catch (ex) {
    console.error('Error occurred while deleting event', ex);
    throw ex;
  }
};

export const getSurveyEvents = async () => {
  let response = null;

  const requestOptions = {
    url: `${apiUrl}/survey`,
    headers: await authHeaders(),
    json: true,
  };

  try {
    response = await request.get(requestOptions);

    if (response && response.success) {
      return response.data.filter(({ type }) => ['treatment', 'scan', 'clinicVisit'].includes(type)).map((event) => {
        const appointmentDate = event.surveyAnswers.find((answer) => answer.key === 'nextAppointment')?.value;
        const currentDate = new Date();
        const eventDate = new Date(appointmentDate);

        let status = SurveyStatus.UPCOMING; // Default to upcoming

        if (eventDate < currentDate) {
          status = SurveyStatus.EXPIRED;
        }

        return {
          id: event.id,
          type: event.type === 'clinicVisit' ? 'clinic_visit' : event.type,
          date: appointmentDate,
          cycle: event.surveyAnswers.find((answer) => answer.key === 'cycle')?.value,
          repeatStatus: event.surveyAnswers.find((answer) => answer.key === 'repeat')?.value,
          status, // Use the determined status
        };
      });
    }
    console.error('Error getting events:', response?.error || 'Unknown error');
    return [];
  } catch (ex) {
    console.error('Error occurred while getting events', ex);
    return [];
  }
};

export async function getCustomSurvey({ surveyTarget, prioritizedSurvey }) {
  const requestOptions = {
    url: `${apiUrl}/survey/customSurvey/${surveyTarget}`,
    headers: await authHeaders(),
    qs: {
      prioritizedSurvey,
    },
    json: true,
    skipNull: true,
  };

  try {
    const { success, data } = await request.get(requestOptions);
    if (!success) {
      throw new Error('something went wrong');
    }
    return data;
  } catch (ex) {
    console.error('Error occurred, while getting custom survey:', ex);
    throw ex;
  }
}

export async function setCustomSurvey({ surveyTarget, ...payload }) {
  const requestOptions = {
    url: `${apiUrl}/survey/customSurvey/${surveyTarget}`,
    headers: await authHeaders(),
    data: payload,
    json: true,
  };

  try {
    const { success } = await request.post(requestOptions);
    if (!success) {
      throw new Error('something went wrong');
    }
  } catch (ex) {
    console.error('Error occurred, while setting custom survey:', ex);
    throw ex;
  }
}
